import React from 'react'
import PropTypes from 'prop-types'

import { Card, CardHeading, CardContent, CardFooter } from '@components/layout/Card'
import { Stack } from '@UILibrary/Stack'
import { Pill } from '@UILibrary/Pill'
import { Text } from '@UILibrary/Text'

import { track } from '@utils/tracking' // Import the track function

export const EmployeeFinancialHealthAreas = ({
  employeeDebtLevelsStatus,
  employeeInvestingStatus,
  employeeSpendingStatus,
}) => {
  const healthAreas = [
    {
      link: '#employee-spending',
      status: employeeSpendingStatus,
      title: 'Employee Spending',
      contentTexts: {
        great: 'Employee spending is lower than benchmark, this means they most likely have some wiggle room in their budget to put money towards financial growth.',
        needs_improvement: "Employees are spending more money than they should to move more easily towards a financially less stressed life. We'd recommend a stronger focus on spending habits in a future workshop.",
        no_data: 'No spending data available.',
      },
    },
    {
      link: '#employee-investing',
      status: employeeInvestingStatus,
      title: 'Employee Investing',
      contentTexts: {
        great: 'Your team has really good savings habits. They are above benchmark and on their way to secure financial futures.',
        needs_improvement: "Your team isn't saving as much as they need, which would correlate to their financial health score being a bit lower than expected. We'd recommend focusing on a savings drive within the business and next workshops to address this.",
        no_data: 'No investing data available.',
      },
    },
    {
      link: '#employee-debt-levels',
      status: employeeDebtLevelsStatus,
      title: 'Employee Debt Level',
      contentTexts: {
        great: 'There is still some debt around, but your team is handling it well. This significantly reduces team stress and increases their focus and productivity. ',
        needs_improvement: "Debt seems to be consuming most of your team's monthly income. This adds a lot of financial stress and might impact retention and engagement. We recommend a strong focus on debt management strategies in the following workshop.",
        no_data: 'No debt data available.',
      },
    },
  ]

  const statusConfig = {
    great: {
      pillColor: 'success',
      pillIcon: 'star',
      pillText: 'Doing great',
    },
    needs_improvement: {
      pillColor: 'warning',
      pillIcon: 'exclamation-triangle',
      pillText: 'Needs Improvement',
    },
    no_data: {
      pillColor: 'secondary_one',
      pillIcon: null,
      pillText: 'No Data',
    },
  }

  const renderPill = (status) => {
    const config = statusConfig[status] || statusConfig.no_data

    return (
      <Pill
        color={config.pillColor}
        shape="square"
        size="small"
        iconBefore={config.pillIcon}
      >
        <Text bodySmall as="span">{config.pillText}</Text>
      </Pill>
    )
  }

  // Function to handle link clicks and track events
  const handleLinkClick = (status, title) => {
    track('Organization Report: Financial Health section click', { status: status, section: title })
  }

  return (
    <div>
      <Stack horizontal spacing="m" wrap>
        {healthAreas.map((area, index) => {
          const status = area.status || 'no_data'
          const contentText = area.contentTexts[status] || 'No data available for this area.'

          return (
            <Card className="content-expand" key={index} style={{ flex: '1 1 30%' }}>
              <CardHeading>
                <Text
                  bodyLarge
                  colour="secondary-main"
                  as="h3"
                  margin={{ bottom: 'xs' }}
                >
                  {area.title}
                </Text>
                {renderPill(status)}
              </CardHeading>
              <CardContent>
                <Text body as="p">
                  {contentText}
                </Text>
              </CardContent>
              <CardFooter>
                <a
                  href={area.link}
                  onClick={() => handleLinkClick(status, area.title)}
                >
                  Show more
                </a>
              </CardFooter>
            </Card>
          )
        })}
      </Stack>
    </div>
  )
}

EmployeeFinancialHealthAreas.propTypes = {
  employeeDebtLevelsStatus: PropTypes.string.isRequired,
  employeeInvestingStatus: PropTypes.string.isRequired,
  employeeSpendingStatus: PropTypes.string.isRequired,
}
